import { useMemo } from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import { isEqual } from "lodash-es";
import moment, { Moment } from "moment-timezone";

import {
    ControlWithFieldsView,
    NewControlWithFieldsView,
    FieldConditionForString,
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    Presence,
    CreatableControlBody,
    StepAllocatedTime,
    UpdatableControlBody,
    SchedulingOccurrenceChangeForDueDateOffsetForMonthOffsetPeriod as SchedulingOccurrenceChange,
    UpdatableControlRecurrenceRule,
    UpdatableRecurrenceRuleWithOffsetForTwoMonthsAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableEveryTwoMonthsRecurrenceRule,
    UpdatableRecurrenceRuleWithOffsetAndFinancialYearForHalfYearAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableHalfYearlyRecurrenceRule,
    UpdatableRecurrenceRuleWithOffsetForMonthAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableMonthlyRecurrenceRule,
    UpdatableRecurrenceRuleWithOffsetAndFinancialYearForQuarterAndDueDateOffsetForMonthOffsetPeriodAndUpdatableDueDateOffsetForUpdatableMonthOffsetPeriod as UpdatableQuarterlyRecurrenceRule,
    UpdatableWeeklyRecurrenceRule,
    UpdatableYearlyRecurrenceRule,
    ControlRecurrenceRule,
    ControlScheduling,
    ControlPolicies,
    ControlPolicy,
    DataSheetCellValue,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import {
    getRowGrouping,
    isGroupingEqual,
} from "components/fieldBuilder/dataSheet";
import { Field, FieldGroup } from "components/fieldBuilder/models";
import { diffFieldGroup } from "components/fieldBuilder/utils/diff";

import {
    DataSheetAssignmentsForControlProgramAndCurrentControl,
    DataSheetGroupingAndControlsAssignedForCurrentControl,
} from "./components/tasks/types";
import {
    ALL_HALF_YEARS,
    ALL_MONTHS,
    ALL_QUARTERS,
    ALL_TWO_MONTHS,
} from "./consts";
import { normalizeRecurrenceRule } from "./utils/normalizeRecurrenceRule";
export const MAX_NOTIFICATION_OFFSET_DAY_COUNT = 999;

// This is the absolute type of the form instance state:
// - all fields derive their value from this structure
// - form validation always returns this structure
// The form state instance is not concerned with view/create/update states and structures
export type ControlViewFormState = ReturnType<typeof toControlViewFormState>;

enum DaysOrHours {
    Days = "days",
    Hours = "hours",
}

export type ControlNotificationUnit = `${DaysOrHours}`;
export type ControlResponsibleNotificationUnit = `${DaysOrHours.Days}`;

export interface AllocatedTimeResponsible extends SimpleAllocatedTime {
    // Time of day at which the responsible should get their notification.
    sendTime: Moment;
    // Unit of responsible's allocated time. This is "days" in the UI.
    unit: ControlResponsibleNotificationUnit;
}

export interface SimpleAllocatedTime {
    value: number;
    unit: ControlNotificationUnit;
}

// A simplified interface to the backend types to be used in the UI, the `Planning` comp in particular.
// The backend stores allocated times in `days` and `microseconds`. We are exposing a `SimpleAllocatedTime`
// that works with the user-visible units of `days` and `hours` and abstract the conversion and updating
// logic away behind the util functions defined below.
export interface SimpleAllocatedTimes {
    responsible: AllocatedTimeResponsible;
    firstReviewer: SimpleAllocatedTime;
    secondReviewer: SimpleAllocatedTime;
}

// More than half of the code here is diff checks. It is not sustainable
// There are better, less verbose ways - such as using a deep object diff library
// We should consider alternatives if making any significant changes

export interface ParsedControlScheduling extends Omit<
    ControlScheduling,
    "dueTime" | "sendTime" | "startDate"
> {
    dueTime: Moment;
    sendTime: Moment;
    startDate: Moment;
    timezoneName: TimezoneKey;
}

export function toParsedControlScheduling(
    controlScheduling: ControlScheduling
): ParsedControlScheduling {
    const { dueTime, startDate, sendTime, timezoneName, ...rest } =
        controlScheduling;
    return {
        ...rest,
        dueTime: moment(dueTime, "HH:mm"),
        sendTime: moment(sendTime, "HH:mm"),
        startDate: moment(startDate, "YYYY-MM-DD"),
        timezoneName: timezoneName as TimezoneKey,
    };
}

export function updateFromParsedControlScheduling(
    controlScheduling: ControlScheduling,
    update: ParsedControlScheduling
): ControlScheduling {
    const { dueTime, sendTime, startDate, ...rest } = update;
    return {
        ...controlScheduling,
        ...rest,
        dueTime: dueTime.format("HH:mm"),
        sendTime: sendTime.format("HH:mm"),
        startDate: startDate.format("YYYY-MM-DD"),
    };
}

function diffToPresence(
    source: Set<string>,
    update: Set<string>
): Record<string, Presence> {
    const diff: Record<string, Presence> = {};

    for (const sourceKey of source) {
        if (!update.has(sourceKey)) {
            diff[sourceKey] = "Absent";
        }
    }

    for (const updateKey of update) {
        if (!source.has(updateKey)) {
            diff[updateKey] = "Present";
        }
    }

    return diff;
}

function diffToOccurrenceChangeOrNull<Occurrence extends string>(
    allOccurrences: Array<Occurrence>,
    source: Partial<Record<Occurrence, DefaultOrCustomOffset>>,
    update: Partial<Record<Occurrence, DefaultOrCustomOffset>>
): Partial<Record<Occurrence, SchedulingOccurrenceChange | null>> {
    const diff: Partial<Record<Occurrence, SchedulingOccurrenceChange | null>> =
        {};

    allOccurrences.forEach((occurrence) => {
        const oldValue = source[occurrence];
        const newValue = update[occurrence];

        if (!isEqual(newValue, oldValue)) {
            // Value is `undefined` when the special occurrence is not scheduled.
            // `null` will unset the special occurrence on the backend.
            diff[occurrence] = newValue ?? null;
        }
    });

    return diff;
}

function fullRule(
    rule: ControlRecurrenceRule
): Exclude<UpdatableControlRecurrenceRule, { kind: "Partial" }> {
    switch (rule.type) {
        case "Date":
            return rule;
        case "Weekly": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        case "Monthly": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        case "EveryTwoMonths": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        case "Quarterly": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        case "HalfYearly": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        case "Yearly": {
            const { type, ...data } = rule;
            return { type, kind: "Full", data };
        }
        default:
            assertNever(rule);
    }
}

function partialRule(
    source: ControlRecurrenceRule,
    update: ControlRecurrenceRule
): Exclude<UpdatableControlRecurrenceRule, { kind: "Full" }> {
    switch (update.type) {
        case "Date":
            return update;
        case "Weekly": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableWeeklyRecurrenceRule = {};

            const updateDays = new Set(updateData.days);
            const sourceDays = new Set(sourceData.days);
            if (!isEqual(updateDays, sourceData)) {
                data.days = diffToPresence(sourceDays, updateDays); // I'm super surprised this line passes type-check
            }
            if (!isEqual(sourceData.periodicity, updateData.periodicity)) {
                data.periodicity = updateData.periodicity;
            }

            return { type, kind: "Partial", data };
        }
        case "Monthly": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableMonthlyRecurrenceRule = {};

            if (!isEqual(sourceData.occurrences, updateData.occurrences)) {
                data.occurrences = diffToOccurrenceChangeOrNull(
                    ALL_MONTHS,
                    sourceData.occurrences,
                    updateData.occurrences
                );
            }
            if (
                !isEqual(
                    sourceData.defaultDueDateOffset,
                    updateData.defaultDueDateOffset
                )
            ) {
                data.defaultDueDateOffset = updateData.defaultDueDateOffset;
            }
            if (!isEqual(sourceData.calendarId, updateData.calendarId)) {
                data.calendarId = updateData.calendarId;
            }

            return { type, kind: "Partial", data };
        }
        case "EveryTwoMonths": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableEveryTwoMonthsRecurrenceRule = {};

            if (!isEqual(sourceData.occurrences, updateData.occurrences)) {
                data.occurrences = diffToOccurrenceChangeOrNull(
                    ALL_TWO_MONTHS,
                    sourceData.occurrences,
                    updateData.occurrences
                );
            }
            if (
                !isEqual(
                    sourceData.defaultDueDateOffset,
                    updateData.defaultDueDateOffset
                )
            ) {
                data.defaultDueDateOffset = updateData.defaultDueDateOffset;
            }
            if (!isEqual(sourceData.calendarId, updateData.calendarId)) {
                data.calendarId = updateData.calendarId;
            }

            return { type, kind: "Partial", data };
        }
        case "Quarterly": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableQuarterlyRecurrenceRule = {};

            if (!isEqual(sourceData.occurrences, updateData.occurrences)) {
                data.occurrences = diffToOccurrenceChangeOrNull(
                    ALL_QUARTERS,
                    sourceData.occurrences,
                    updateData.occurrences
                );
            }
            if (
                !isEqual(
                    sourceData.defaultDueDateOffset,
                    updateData.defaultDueDateOffset
                )
            ) {
                data.defaultDueDateOffset = updateData.defaultDueDateOffset;
            }
            if (
                !isEqual(sourceData.financialYearId, updateData.financialYearId)
            ) {
                data.financialYearId = updateData.financialYearId;
            }
            if (!isEqual(sourceData.calendarId, updateData.calendarId)) {
                data.calendarId = updateData.calendarId;
            }

            return { type, kind: "Partial", data };
        }
        case "HalfYearly": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableHalfYearlyRecurrenceRule = {};

            if (!isEqual(sourceData.occurrences, updateData.occurrences)) {
                data.occurrences = diffToOccurrenceChangeOrNull(
                    ALL_HALF_YEARS,
                    sourceData.occurrences,
                    updateData.occurrences
                );
            }
            if (
                !isEqual(
                    sourceData.defaultDueDateOffset,
                    updateData.defaultDueDateOffset
                )
            ) {
                data.defaultDueDateOffset = updateData.defaultDueDateOffset;
            }
            if (
                !isEqual(sourceData.financialYearId, updateData.financialYearId)
            ) {
                data.financialYearId = updateData.financialYearId;
            }
            if (!isEqual(sourceData.calendarId, updateData.calendarId)) {
                data.calendarId = updateData.calendarId;
            }

            return { type, kind: "Partial", data };
        }
        case "Yearly": {
            if (source.type !== update.type) {
                throw new Error("Not a partial change!");
            }
            const { type, ...updateData } = update;
            const { type: _type, ...sourceData } = source;
            const data: UpdatableYearlyRecurrenceRule = {};

            if (
                !isEqual(
                    sourceData.defaultDueDateOffset,
                    updateData.defaultDueDateOffset
                )
            ) {
                data.defaultDueDateOffset = updateData.defaultDueDateOffset;
            }
            if (!isEqual(sourceData.periodicity, updateData.periodicity)) {
                data.periodicity = updateData.periodicity;
            }
            if (
                !isEqual(sourceData.financialYearId, updateData.financialYearId)
            ) {
                data.financialYearId = updateData.financialYearId;
            }
            if (!isEqual(sourceData.calendarId, updateData.calendarId)) {
                data.calendarId = updateData.calendarId;
            }

            return { type, kind: "Partial", data };
        }
        default:
            throw new Error();
    }
}

interface StepToDiff<FieldType, FieldValue, Assignment> {
    fieldGroup: FieldGroup<FieldType, FieldValue>;
    allocatedTime: StepAllocatedTime;
    assignment: Record<string, Assignment>;
}

function diffStep<FieldType, FieldValue, Assignment>(
    source: StepToDiff<FieldType, FieldValue, Assignment>,
    update: StepToDiff<FieldType, FieldValue, Assignment>
) {
    const diff: {
        fieldGroup?: ReturnType<typeof diffFieldGroup<FieldType, FieldValue>>;
        allocatedTime?: StepAllocatedTime;
        assignments?: Record<string, Assignment | null>;
    } = {};

    if (!isEqual(source.fieldGroup, update.fieldGroup)) {
        diff.fieldGroup = diffFieldGroup(source.fieldGroup, update.fieldGroup);
    }
    if (!isEqual(source.allocatedTime, update.allocatedTime)) {
        diff.allocatedTime = update.allocatedTime;
    }
    if (!isEqual(source.assignment, update.assignment)) {
        diff.assignments = {};

        for (const [assignmentGroupId, updateAssignment] of Object.entries(
            update.assignment
        )) {
            if (
                !isEqual(source.assignment[assignmentGroupId], updateAssignment)
            ) {
                diff.assignments[assignmentGroupId] = updateAssignment;
            }
        }
        for (const assignmentGroupId of Object.keys(source.assignment)) {
            if (!update.assignment[assignmentGroupId]) {
                diff.assignments[assignmentGroupId] = null;
            }
        }
    }

    return diff;
}

// Picked from legacy defaults
const DEFAULT_SCHEDULING: ControlScheduling = {
    timezoneName: "Europe/Paris",
    dueTime: "23:59",
    startDate: moment().add(1, "day").format("YYYY-MM-DD"),
    rule: { type: "Date" },
    sendTime: "07:00",
    coveredPeriod: null,
    remindersBefore: [],
    remindersAfter: [],
};

function normalizeFieldConditions<
    T extends { condition?: FieldConditionForString | null },
>(fields: T[]) {
    return fields.map((field) => ({
        ...field,
        condition: field.condition ?? null,
    }));
}

export const toControlViewFormState = (
    control: ControlWithFieldsView | NewControlWithFieldsView
) => {
    const analytics: Record<string, Field<unknown, string>[]> = {};
    const { initialStep, subsequentSteps } = control.stepGroup;
    const rawScheduling = control.scheduling ?? DEFAULT_SCHEDULING;
    const scheduling: ControlScheduling = {
        ...rawScheduling,
        coveredPeriod: rawScheduling.coveredPeriod ?? null,
        rule: normalizeRecurrenceRule(rawScheduling.rule),
    };
    return {
        ...control,
        controlProgramId: control.controlProgramId ?? null,
        description: control.description ?? "",
        triggeredBy: control.triggeredBy ?? null,
        scheduling,
        stepGroup: {
            ...control.stepGroup,
            initialStep: {
                ...initialStep,
                assignment: Object.fromEntries(
                    Object.entries(initialStep.assignment).map(
                        ([id, assignment]) => [
                            id,
                            assignment.type === "Pool"
                                ? {
                                      ...assignment,
                                      defaultClaimantId:
                                          assignment.defaultClaimantId ?? null,
                                  }
                                : assignment,
                        ]
                    )
                ),
                fieldGroup: {
                    ...initialStep.fieldGroup,
                    fields: normalizeFieldConditions(
                        initialStep.fieldGroup.fields
                    ),
                },
            },
            subsequentSteps: subsequentSteps.map((step) => ({
                ...step,
                fieldGroup: {
                    ...step.fieldGroup,
                    fields: normalizeFieldConditions(step.fieldGroup.fields),
                },
            })),
        },
        sendImmediately: false,
        analytics,
    };
};

/**
 * Applies the organisation policy for the login protection flag to a form state.
 *
 * The backend already stores the enforced value on every control, this keeps the
 * form in line with it.
 */
export const applyControlPolicies = (
    formState: ControlViewFormState,
    policies: ControlPolicies
): ControlViewFormState => ({
    ...formState,
    assignmentSettings: {
        ...formState.assignmentSettings,
        loginProtectionEnabled: applyControlPolicy(
            formState.assignmentSettings.loginProtectionEnabled,
            policies.loginProtection
        ),
        segregationOfDutiesEnabled: applyControlPolicy(
            formState.assignmentSettings.segregationOfDutiesEnabled,
            policies.segregationOfDuties
        ),
    },
});

function applyControlPolicy(value: boolean, policy: ControlPolicy): boolean {
    switch (policy.mode) {
        case "Default":
            return value;
        case "Enforced":
            return true;
        default:
            return assertNever(policy);
    }
}

export function toUpdateControlPayload(
    rawSource: ControlWithFieldsView | NewControlWithFieldsView,
    update: ControlViewFormState
): UpdatableControlBody {
    const source = toControlViewFormState(rawSource);
    // Here we perform a deep comparison of source & update, then returns only the minimal changes
    const patch: UpdatableControlBody = {
        analytics: update.analytics,
    };

    if (!isEqual(source.title, update.title)) {
        patch.title = update.title;
    }
    if (!isEqual(source.description, update.description)) {
        patch.description = update.description;
    }
    if (!isEqual(source.controlProgramId, update.controlProgramId)) {
        if (update.controlProgramId === null) {
            // This is assumed to be validated by the schema
            // Would be fixed by having something like ValidatedControlViewFormState, ideally inferred from the schema, but it is currently incomplete
            throw new Error("Expected update.controlProgramId to be non-null!");
        }
        patch.controlProgramId = update.controlProgramId;
    }
    const sourceTagIds = new Set(source.tagIds);
    const updateTagIds = new Set(update.tagIds);
    if (!isEqual(source.tagIds, update.tagIds)) {
        patch.tagIds = diffToPresence(sourceTagIds, updateTagIds);
    }
    if (!isEqual(source.triggeredBy, update.triggeredBy)) {
        patch.triggeredBy = update.triggeredBy;
    }
    if (!isEqual(source.scheduling, update.scheduling)) {
        patch.scheduling = {};
        if (
            !isEqual(
                source.scheduling.timezoneName,
                update.scheduling.timezoneName
            )
        ) {
            patch.scheduling.timezoneName = update.scheduling.timezoneName;
        }
        if (!isEqual(source.scheduling.sendTime, update.scheduling.sendTime)) {
            patch.scheduling.sendTime = update.scheduling.sendTime;
        }
        if (!isEqual(source.scheduling.dueTime, update.scheduling.dueTime)) {
            patch.scheduling.dueTime = update.scheduling.dueTime;
        }
        if (
            !isEqual(source.scheduling.startDate, update.scheduling.startDate)
        ) {
            patch.scheduling.startDate = update.scheduling.startDate;
        }
        if (!isEqual(source.scheduling.rule, update.scheduling.rule)) {
            if (source.scheduling.rule.type !== update.scheduling.rule.type) {
                patch.scheduling.rule = fullRule(update.scheduling.rule);
            } else {
                patch.scheduling.rule = partialRule(
                    source.scheduling.rule,
                    update.scheduling.rule
                );
            }
        }
        if (
            !isEqual(
                source.scheduling.coveredPeriod,
                update.scheduling.coveredPeriod
            )
        ) {
            patch.scheduling.coveredPeriod = update.scheduling.coveredPeriod;
        }
        if (
            !isEqual(
                source.scheduling.remindersBefore,
                update.scheduling.remindersBefore
            )
        ) {
            patch.scheduling.remindersBefore =
                update.scheduling.remindersBefore;
        }
        if (
            !isEqual(
                source.scheduling.remindersAfter,
                update.scheduling.remindersAfter
            )
        ) {
            patch.scheduling.remindersAfter = update.scheduling.remindersAfter;
        }
    }
    if (!isEqual(source.stepGroup, update.stepGroup)) {
        patch.stepGroup = {};
        if (
            !isEqual(source.stepGroup.initialStep, update.stepGroup.initialStep)
        ) {
            patch.stepGroup.initialStep = diffStep(
                source.stepGroup.initialStep,
                update.stepGroup.initialStep
            );
        }
        if (
            !isEqual(
                source.stepGroup.subsequentSteps[0],
                update.stepGroup.subsequentSteps[0]
            )
        ) {
            patch.stepGroup.subsequentSteps = {
                ...patch.stepGroup.subsequentSteps,
                SubsequentStep0: diffStep(
                    source.stepGroup.subsequentSteps[0],
                    update.stepGroup.subsequentSteps[0]
                ),
            };
        }
        if (
            !isEqual(
                source.stepGroup.subsequentSteps[1],
                update.stepGroup.subsequentSteps[1]
            )
        ) {
            patch.stepGroup.subsequentSteps = {
                ...patch.stepGroup.subsequentSteps,
                SubsequentStep1: diffStep(
                    source.stepGroup.subsequentSteps[1],
                    update.stepGroup.subsequentSteps[1]
                ),
            };
        }

        if (!isEqual(source.stepGroup.entityIds, update.stepGroup.entityIds)) {
            const sourceEntityIds = source.stepGroup.entityIds;
            const updateEntityIds = update.stepGroup.entityIds;
            const patchEntityIds: Record<string, number | null> = {};
            const initialExitingAssignment =
                update.stepGroup.initialStep.assignment;

            // Update: allow only for assignment groups that still exist
            for (const [assignmentGroupId, updatedEntityId] of Object.entries(
                updateEntityIds
            )) {
                if (initialExitingAssignment[assignmentGroupId]) {
                    if (
                        !isEqual(
                            sourceEntityIds[assignmentGroupId],
                            updatedEntityId
                        )
                    ) {
                        patchEntityIds[assignmentGroupId] = updatedEntityId;
                    }
                }
            }

            // Unlink: send null only for assignment groups that still exist and were unlinked
            for (const assignmentGroupId of Object.keys(sourceEntityIds)) {
                if (initialExitingAssignment[assignmentGroupId]) {
                    if (updateEntityIds[assignmentGroupId] === undefined) {
                        patchEntityIds[assignmentGroupId] = null;
                    }
                }
            }

            if (Object.keys(patchEntityIds).length > 0) {
                patch.stepGroup.entityIds = patchEntityIds;
            }
        }

        if (
            !isEqual(
                source.stepGroup.notificationRecipients,
                update.stepGroup.notificationRecipients
            )
        ) {
            if (
                Object.entries(update.stepGroup.notificationRecipients).length >
                0
            ) {
                patch.stepGroup.notificationRecipients = {};
            }

            for (const [assignmentGroupId, recipients] of Object.entries(
                update.stepGroup.notificationRecipients
            )) {
                if (
                    !isEqual(
                        source.stepGroup.notificationRecipients[
                            assignmentGroupId
                        ],
                        recipients
                    )
                ) {
                    patch.stepGroup.notificationRecipients![assignmentGroupId] =
                        recipients;
                }
            }
        }
    }

    if (!isEqual(source.assignmentSettings, update.assignmentSettings)) {
        patch.assignmentSettings = {};

        // Assignment groups are only sent when their tags actually changed: the
        // backend authorizes every group listed here against the entity access
        // rights of the user, so no-op entries would turn an otherwise allowed
        // assignment update into a forbidden one.
        const tagIdsPatch: Record<string, Record<string, Presence>> = {};
        for (const [assignmentGroupId, tagIds] of Object.entries(
            update.assignmentSettings.tagIds
        )) {
            const sourceTagIds = new Set(
                source.assignmentSettings.tagIds[assignmentGroupId]
            );
            const updateTagIds = new Set(tagIds);
            const tagIdsDiff = diffToPresence(sourceTagIds, updateTagIds);
            if (Object.keys(tagIdsDiff).length > 0) {
                tagIdsPatch[assignmentGroupId] = tagIdsDiff;
            }
        }
        if (Object.keys(tagIdsPatch).length > 0) {
            patch.assignmentSettings.tagIds = tagIdsPatch;
        }

        if (
            !isEqual(
                source.assignmentSettings.loginProtectionEnabled,
                update.assignmentSettings.loginProtectionEnabled
            )
        ) {
            patch.assignmentSettings.loginProtectionEnabled =
                update.assignmentSettings.loginProtectionEnabled;
        }

        if (
            !isEqual(
                source.assignmentSettings.segregationOfDutiesEnabled,
                update.assignmentSettings.segregationOfDutiesEnabled
            )
        ) {
            patch.assignmentSettings.segregationOfDutiesEnabled =
                update.assignmentSettings.segregationOfDutiesEnabled;
        }
        // No need to add the missing tag entries (the ones in source but not in update)
        // since it means the assignmentGroup have been removed.
    }

    return patch;
}

export function toCreateControlPayload(
    update: ControlViewFormState
): CreatableControlBody {
    // This is assumed to be validated by the schema
    // Would be fixed by having something like ValidatedControlViewFormState, ideally inferred from the schema, but it is currently incomplete
    if (update.controlProgramId === null) {
        throw new Error("Expected update.controlProgramId to be non-null!");
    }
    if (!update.scheduling) {
        throw new Error("Expected update.scheduling to be non-null!");
    }
    const creatableControlBody: CreatableControlBody = {
        id: update.id,
        title: update.title,
        stepGroup: update.stepGroup,
        assignmentSettings: update.assignmentSettings,
        tagIds: update.tagIds,
        description: update.description,
        scheduling: update.scheduling,
        controlProgramId: update.controlProgramId,
        triggeredBy: update.triggeredBy,
        analytics: update.analytics,
    };
    return creatableControlBody;
}

export const useDataSheetAssignmentsForCurrentControl = (
    fields: readonly {
        id: string;
        fieldType: { type: string; dataSheetId?: string; grouping?: unknown[] };
    }[],
    dataSheetAssignmentsData: DataSheetAssignmentsListDto
) => {
    const dataSheetAssignments =
        useMemo((): DataSheetAssignmentsForControlProgramAndCurrentControl => {
            const { content, linkedResources } = dataSheetAssignmentsData;
            return {
                content: content.map(
                    (
                        assignments
                    ): DataSheetAssignmentsForControlProgramAndCurrentControl["content"][number] => {
                        const {
                            resource: { dataSheetId, groupings },
                            actions,
                        } = assignments;
                        return {
                            resource: {
                                dataSheetId,
                                groupings: groupings.map(
                                    (
                                        grouping
                                    ): DataSheetGroupingAndControlsAssignedForCurrentControl => {
                                        const { columns, assignedToControls } =
                                            grouping;
                                        const dataSheet =
                                            linkedResources.dataSheets.find(
                                                (ds) => ds.id === dataSheetId
                                            )!;
                                        const groupingColumns =
                                            dataSheet.columns.filter(
                                                (col) => col.groupingColumn
                                            );
                                        return {
                                            columns,
                                            assignedToControls,
                                            currentControlFieldIds: fields
                                                .filter(
                                                    (field) =>
                                                        // Field is a datasheet field
                                                        field.fieldType.type ===
                                                            "DataSheet" &&
                                                        // ... with a matching datasheet ID
                                                        field.fieldType
                                                            .dataSheetId ===
                                                            dataSheetId &&
                                                        // ... and the field's grouping is equal to the filtered grouping
                                                        isGroupingEqual(
                                                            field.fieldType
                                                                .grouping as (DataSheetCellValue | null)[],
                                                            getRowGrouping(
                                                                groupingColumns,
                                                                grouping
                                                            )
                                                        )
                                                )
                                                .map((field) => field.id),
                                        };
                                    }
                                ),
                            },
                            actions,
                        };
                    }
                ),
                linkedResources,
            };
        }, [fields, dataSheetAssignmentsData]);

    return dataSheetAssignments;
};
